import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { dashboardAPI, analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    BarChart3,
    TrendingUp,
    Users,
    Sparkles
} from 'lucide-react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from 'recharts';

export default function AnalyticsPage() {
    const [metrics, setMetrics] = useState(null);
    const [heatmapData, setHeatmapData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [textToAnalyze, setTextToAnalyze] = useState('');
    const [analysisResult, setAnalysisResult] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [metricsRes, heatmapRes] = await Promise.all([
                dashboardAPI.getMetrics(),
                dashboardAPI.getHeatmap()
            ]);
            setMetrics(metricsRes.data);
            setHeatmapData(heatmapRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        if (!textToAnalyze.trim()) return;
        try {
            setAnalyzing(true);
            const response = await analyticsAPI.analyzeSentiment({
                text: textToAnalyze,
                context: 'feedback'
            });
            setAnalysisResult(response.data);
        } catch (error) {
            console.error('Analysis failed:', error);
        } finally {
            setAnalyzing(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading analytics...</p>
                </div>
            </div>
        );
    }

    // Calculate pie data from real metrics
    const engagementScore = metrics?.avg_engagement_score || 0;
    const pieData = engagementScore > 0 ? [
        { name: 'Engaged', value: Math.max(0, engagementScore), color: '#10B981' },
        { name: 'Neutral', value: Math.max(0, Math.min(0.3, 1 - engagementScore)), color: '#F59E0B' },
        { name: 'Needs Attention', value: Math.max(0, 1 - engagementScore - 0.3), color: '#F47266' },
    ].filter(item => item.value > 0) : [];

    return (
        <div className="p-8" data-testid="analytics-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                        <BarChart3 className="w-8 h-8 text-indigo-600" />
                        Analytics
                    </h1>
                    <p className="text-stone-500 mt-1">
                        Deep insights into workforce sentiment, engagement, and well-being.
                    </p>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <Card className="bento-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-stone-500 mb-1">Avg. Sentiment</p>
                                    <p className="text-3xl font-bold font-heading text-indigo-600">
                                        {((metrics?.avg_sentiment_score || 0) * 100).toFixed(0)}%
                                    </p>
                                </div>
                                <TrendingUp className="w-8 h-8 text-indigo-200" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-stone-500 mb-1">Engagement Rate</p>
                                    <p className="text-3xl font-bold font-heading text-emerald-600">
                                        {((metrics?.avg_engagement_score || 0) * 100).toFixed(0)}%
                                    </p>
                                </div>
                                <Users className="w-8 h-8 text-emerald-200" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-stone-500 mb-1">Well-being Index</p>
                                    <p className="text-3xl font-bold font-heading text-amber-600">
                                        {((metrics?.avg_wellbeing_score || 0) * 100).toFixed(0)}%
                                    </p>
                                </div>
                                <BarChart3 className="w-8 h-8 text-amber-200" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Trend Chart */}
                    <Card className="lg:col-span-2 bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Sentiment Trends</CardTitle>
                            <CardDescription>7-day rolling sentiment analysis</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={metrics?.sentiment_trend || []}>
                                        <defs>
                                            <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12, fill: '#78716c' }}
                                            tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis
                                            domain={[0, 1]}
                                            tick={{ fontSize: 12, fill: '#78716c' }}
                                            tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                                        />
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (active && payload?.[0]) {
                                                    return (
                                                        <div className="glass p-3 rounded-xl">
                                                            <p className="text-sm font-medium">
                                                                {(payload[0].value * 100).toFixed(0)}%
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="score"
                                            stroke="#4F46E5"
                                            strokeWidth={2}
                                            fill="url(#colorScore)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Engagement Pie */}
                    <Card className="bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Engagement Distribution</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={70}
                                            dataKey="value"
                                            paddingAngle={3}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={index} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (active && payload?.[0]) {
                                                    return (
                                                        <div className="glass p-2 rounded-lg text-sm">
                                                            {payload[0].name}: {(payload[0].value * 100).toFixed(0)}%
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex justify-center gap-4 mt-2">
                                {pieData.map((item) => (
                                    <div key={item.name} className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                        <span className="text-xs text-stone-600">{item.name}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Text Analysis Tool */}
                <Card className="bento-card">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-indigo-600" />
                            AI Text Analysis
                        </CardTitle>
                        <CardDescription>
                            Analyze any text for sentiment, emotions, and psychometric indicators.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Textarea
                            placeholder="Paste feedback, survey responses, or any text to analyze..."
                            value={textToAnalyze}
                            onChange={(e) => setTextToAnalyze(e.target.value)}
                            className="min-h-[100px] rounded-xl"
                            data-testid="analytics-text-input"
                        />
                        <Button
                            onClick={handleAnalyze}
                            disabled={analyzing || !textToAnalyze.trim()}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="analytics-analyze-btn"
                        >
                            {analyzing ? (
                                <span className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Analyzing...
                                </span>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Analyze Text
                                </>
                            )}
                        </Button>

                        {analysisResult && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-stone-50 rounded-xl space-y-4"
                            >
                                <div className="flex items-center gap-4">
                                    <Badge className={`capitalize ${
                                        analysisResult.sentiment === 'positive' ? 'bg-emerald-100 text-emerald-800' :
                                        analysisResult.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                        'bg-stone-100 text-stone-800'
                                    }`}>
                                        {analysisResult.sentiment}
                                    </Badge>
                                    <span className="text-sm text-stone-600">
                                        Score: <span className="font-mono font-medium">{analysisResult.score?.toFixed(2)}</span>
                                    </span>
                                    <span className="text-sm text-stone-600">
                                        Confidence: <span className="font-mono font-medium">{(analysisResult.confidence * 100).toFixed(0)}%</span>
                                    </span>
                                </div>

                                {analysisResult.emotions && Object.keys(analysisResult.emotions).length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-2">Emotions Detected</p>
                                        <div className="grid grid-cols-5 gap-2">
                                            {Object.entries(analysisResult.emotions).map(([emotion, value]) => (
                                                <div key={emotion} className="text-center">
                                                    <Progress value={value * 100} className="h-1.5 mb-1" />
                                                    <p className="text-xs text-stone-500 capitalize">{emotion}</p>
                                                    <p className="text-xs font-mono text-stone-700">{(value * 100).toFixed(0)}%</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {analysisResult.key_phrases?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-2">Key Phrases</p>
                                        <div className="flex flex-wrap gap-2">
                                            {analysisResult.key_phrases.map((phrase, i) => (
                                                <Badge key={i} variant="outline" className="text-xs">
                                                    {phrase}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}
